import {
  ANTHROPIC_FABLE_5_1_MODEL_ID,
  ANTHROPIC_FABLE_5_1_MODEL_REF,
  ANTHROPIC_FABLE_5_MODEL_ID,
  ANTHROPIC_FABLE_5_MODEL_REF,
} from "./anthropic";
import {
  DEEPSEEK_V4_PRO_AZURE_MODEL_ID,
  DEEPSEEK_V4_PRO_AZURE_MODEL_REF,
  KIMI_K26_AZURE_MODEL_ID,
  KIMI_K26_AZURE_MODEL_REF,
} from "./azureFoundry";
import {
  BEDROCK_ANTHROPIC_FABLE_5_1_MODEL_ID,
  BEDROCK_ANTHROPIC_FABLE_5_1_MODEL_REF,
  BEDROCK_ANTHROPIC_FABLE_5_MODEL_ID,
  BEDROCK_ANTHROPIC_FABLE_5_MODEL_REF,
} from "./bedrockAnthropic";
import {
  BEDROCK_OPENAI_GPT_5_4_MODEL_ID,
  BEDROCK_OPENAI_GPT_5_4_MODEL_REF,
  BEDROCK_OPENAI_GPT_5_5_MODEL_ID,
  BEDROCK_OPENAI_GPT_5_5_MODEL_REF,
  BEDROCK_OPENAI_GPT_5_6_SOL_MODEL_ID,
  BEDROCK_OPENAI_GPT_5_6_SOL_MODEL_REF,
} from "./bedrockOpenAi";
import { BEDROCK_CONVERSE_OPUS_5_5_MODEL_ID } from "./claudeOpus";
import {
  GLM_52_FAST_MODEL_REF,
  GLM_52_FAST_OPENROUTER_MODEL_ALIAS,
  GLM_52_MODEL_REF,
  GLM_52_OPENROUTER_MODEL_ID,
} from "./glmOpenRouter";
import {
  GROK_45_MODEL_REF,
  GROK_45_OPENROUTER_MODEL_ID,
} from "./grokOpenRouter";
import {
  KIMI_K3_MODEL_REF,
  KIMI_K3_OPENROUTER_MODEL_ID,
} from "./kimiK3OpenRouter";
import {
  OPENAI_GPT_5_6_SOL_MODEL_ID,
  OPENAI_GPT_6_ASTRA_MODEL_ID,
  OPENAI_GPT_6_SOL_MODEL_ID,
} from "./openAi";

/**
 * Per-model context window sizes (input tokens).
 *
 * Used for validation UIs that need to clamp user-chosen values to a
 * fraction of the model's max context — e.g. compact thresholds must stay
 * below the real window or the actor will context-overflow before compacting.
 *
 * Values are the practical max input tokens for the model's default
 * invocation mode. Long-context variants (e.g. Claude's 1M beta) are listed
 * under distinct entries only when they ship as separate model refs.
 */
export const MODEL_CONTEXT_WINDOWS: Record<string, number> = {
  // Azure AI Foundry serverless deployments
  [DEEPSEEK_V4_PRO_AZURE_MODEL_ID]: 1_048_576,
  [DEEPSEEK_V4_PRO_AZURE_MODEL_REF]: 1_048_576,
  // Kimi K2.6: 262_144 per the Azure model card (262.144k) / Moonshot docs (256K).
  [KIMI_K26_AZURE_MODEL_ID]: 262_144,
  [KIMI_K26_AZURE_MODEL_REF]: 262_144,

  // Moonshot AI via OpenRouter
  [KIMI_K3_OPENROUTER_MODEL_ID]: 1_048_576,
  [KIMI_K3_MODEL_REF]: 1_048_576,

  // Z.ai GLM via OpenRouter
  [GLM_52_OPENROUTER_MODEL_ID]: 1_048_576,
  [GLM_52_MODEL_REF]: 1_048_576,
  [GLM_52_FAST_OPENROUTER_MODEL_ALIAS]: 1_048_576,
  [GLM_52_FAST_MODEL_REF]: 1_048_576,

  // Anthropic direct — Claude 4.5+ Sonnet/Opus ship with 1M context
  // (Sonnet 4.5 via `context-1m-2025-08-07` beta header; 4.6/4.7/4.8 native).
  "anthropic/claude-opus-4-8": 1_000_000,
  "claude-haiku-4-5": 200_000,
  "claude-haiku-4-5-20251001": 200_000,
  "claude-opus-4-5": 1_000_000,
  "claude-opus-4-6": 1_000_000,
  "claude-opus-4-6-20260220": 1_000_000,
  "claude-opus-4-7": 1_000_000,
  "claude-opus-4-8": 1_000_000,
  [ANTHROPIC_FABLE_5_MODEL_ID]: 1_000_000,
  [ANTHROPIC_FABLE_5_1_MODEL_ID]: 1_000_000,
  [ANTHROPIC_FABLE_5_MODEL_REF]: 1_000_000,
  [ANTHROPIC_FABLE_5_1_MODEL_REF]: 1_000_000,
  "claude-sonnet-4-5": 1_000_000,
  "claude-sonnet-4-5-20250929": 1_000_000,
  "claude-sonnet-4-6": 1_000_000,
  "claude-sonnet-4-6-20260220": 1_000_000,
  "claude-sonnet-5": 1_000_000,

  // Anthropic via Bedrock — same 1M window once `anthropicBeta:
  // ["context-1m-2025-08-07"]` is passed on the InvokeModel body.
  "global.anthropic.claude-fable-5": 1_000_000,
  "global.anthropic.claude-fable-5-1": 1_000_000,
  "global.anthropic.claude-haiku-4-5-20251001-v1:0": 200_000,
  "global.anthropic.claude-opus-4-5-20251101-v1:0": 1_000_000,
  "global.anthropic.claude-opus-4-6-v1": 1_000_000,
  "global.anthropic.claude-opus-4-7": 1_000_000,
  "global.anthropic.claude-opus-4-8": 1_000_000,
  // Opus 5 is natively 1M on Bedrock — no `context-1m` beta needed (AWS model
  // card "Context window: 1M tokens", retrieved 2026-07-27).
  "global.anthropic.claude-opus-5": 1_000_000,
  // Opus 5.5 is natively 1M as well (AWS model card and Anthropic migration
  // guide, retrieved 2026-09-25: no context-window beta header is needed).
  [BEDROCK_CONVERSE_OPUS_5_5_MODEL_ID]: 1_000_000,
  "global.anthropic.claude-sonnet-4-5-20250929-v1:0": 1_000_000,
  "global.anthropic.claude-sonnet-4-6": 1_000_000,
  [BEDROCK_ANTHROPIC_FABLE_5_MODEL_ID]: 1_000_000,
  [BEDROCK_ANTHROPIC_FABLE_5_1_MODEL_ID]: 1_000_000,
  [BEDROCK_ANTHROPIC_FABLE_5_MODEL_REF]: 1_000_000,
  [BEDROCK_ANTHROPIC_FABLE_5_1_MODEL_REF]: 1_000_000,
  "global.anthropic.claude-sonnet-5": 1_000_000,

  // DeepSeek via OpenRouter
  "deepseek/deepseek-v4-pro": 1_048_576,
  "openrouter/deepseek/deepseek-v4-pro": 1_048_576,

  // Xiaomi via OpenRouter
  "openrouter/xiaomi/mimo-v2.5-pro": 1_048_576,
  "xiaomi/mimo-v2.5-pro": 1_048_576,

  // Amazon Nova
  "global.amazon.nova-2-lite-v1:0": 300_000,

  // Google Gemini
  "gemini-2.5-flash": 1_048_576,
  "gemini-2.5-flash-lite": 1_048_576,
  "gemini-2.5-pro": 1_048_576,
  "gemini-3-flash-preview": 1_048_576,
  "gemini-3.1-flash-lite-preview": 1_048_576,
  "gemini-3.1-pro-preview": 1_048_576,
  "gemini-3.1-pro-preview-customtools": 1_048_576,
  "gemini-3.5-flash": 1_048_576,

  // MiniMax via OpenRouter
  "minimax/minimax-m2.7": 196_608,
  "minimax/minimax-m3": 524_288,
  "openrouter/minimax/minimax-m2.7": 196_608,
  "openrouter/minimax/minimax-m3": 524_288,

  // OpenAI
  "gpt-5-mini": 400_000,
  "gpt-5.1-2025-11-13": 400_000,
  "gpt-5.1-codex-max": 400_000,
  "gpt-5.1-codex-mini": 400_000,
  "gpt-5.3-codex": 272_000,
  "gpt-5.4": 272_000,
  "gpt-5.4-2025-12-11": 272_000,
  "gpt-5.4-codex": 272_000,
  "gpt-5.4-pro": 272_000,
  // Gpt-5.5 (released 2026-04-23) — 1.05M context, 128k max output, automatic
  // Prompt caching at ≥1024-token prefixes (no markers needed; SDK no-ops the
  // Cache_control injection for openai/* via getCacheAnnotation).
  "gpt-5.5": 1_050_000,
  "gpt-5.5-2026-04-23": 1_050_000,
  "gpt-5.5-pro": 1_050_000,
  "gpt-5.5-pro-2026-04-23": 1_050_000,
  [BEDROCK_OPENAI_GPT_5_4_MODEL_ID]: 272_000,
  [BEDROCK_OPENAI_GPT_5_4_MODEL_REF]: 272_000,
  [BEDROCK_OPENAI_GPT_5_5_MODEL_ID]: 272_000,
  [BEDROCK_OPENAI_GPT_5_5_MODEL_REF]: 272_000,
  [BEDROCK_OPENAI_GPT_5_6_SOL_MODEL_ID]: 272_000,
  [BEDROCK_OPENAI_GPT_5_6_SOL_MODEL_REF]: 272_000,
  [OPENAI_GPT_5_6_SOL_MODEL_ID]: 1_050_000,
  [OPENAI_GPT_6_ASTRA_MODEL_ID]: 1_050_000,
  [OPENAI_GPT_6_SOL_MODEL_ID]: 1_050_000,

  // XAI
  [GROK_45_OPENROUTER_MODEL_ID]: 500_000,
  [GROK_45_MODEL_REF]: 500_000,
  "xai/grok-4.1-fast-non-reasoning": 256_000,
  "xai/grok-4.1-fast-reasoning": 256_000,
  "xai/grok-4.20-non-reasoning": 256_000,
};

/** Conservative fallback for unrecognized model refs. */
export const DEFAULT_CONTEXT_WINDOW_TOKENS = 128_000;

/**
 * Resolve the context window (max input tokens) for a model ref.
 * Matches exact entries first; falls back to {@link DEFAULT_CONTEXT_WINDOW_TOKENS}.
 */
export function getContextWindowTokens(
  modelRef: string | null | undefined,
): number {
  if (modelRef == null) {
    return DEFAULT_CONTEXT_WINDOW_TOKENS;
  }
  const exact = MODEL_CONTEXT_WINDOWS[modelRef];
  if (exact != null) {
    return exact;
  }
  // `provider/modelId` shaped refs — strip provider prefix and retry
  const slashIdx = modelRef.indexOf("/");
  if (slashIdx > 0) {
    const tail = modelRef.slice(slashIdx + 1);
    const tailMatch = MODEL_CONTEXT_WINDOWS[tail];
    if (tailMatch != null) {
      return tailMatch;
    }
  }
  return DEFAULT_CONTEXT_WINDOW_TOKENS;
}

/**
 * Upper bound for user-configurable token-budgets expressed as a fraction of
 * the window. Compact thresholds, retrieval budgets, etc. should never reach
 * the full window — the actor needs headroom to emit its response.
 */
export const SAFE_CONTEXT_FRACTION = 0.9;

/** Max safe value for a compact threshold on the given model. */
export function getMaxCompactThresholdForModel(
  modelRef: string | null | undefined,
): number {
  return Math.floor(getContextWindowTokens(modelRef) * SAFE_CONTEXT_FRACTION);
}
